import wx from "weixin-js-sdk";
import { Event } from "~/enum/event";
import DeviceUtils from "./device-utils";
import EventEmitter from "./event";

declare global {
  interface Window {
    jsClientCallBack: Record<string, (...args: any[]) => any>;
    AndroidNativeApi?: Record<string, (...args: any[]) => any>;
    webkit?: {
      messageHandlers?: {
        jsBridge?: {
          postMessage: (data: any) => void;
        };
      };
    };
  }
  interface CallbackData {
    cbName?: string;
    [key: string]: any;
  }
}

enum ClientMethods {
  GO_BACK = "goBack", // 返回上一页
  ASK_LAWYER = "askLawyer", // 问律师
  GET_VOICE = "getVoice", // 语音输入
  GET_VIP = "getVip", // 开通vip
  CONTRACT_DOWNLOAD = "contractDownload", // 合同下载
  OPEN_LINK = "openLink", // 打开链接
  OPEN_SHARE = "openShare", // 打开分享
  APPLY_STORE_AUTH = "applyStoreAuth", // 申请储存授权
  GO_WX_UPLOAD = "goWxUpload", // 打开微信上传文件
  LAWYER_LETTER_ORDER_PAY = "lawyerLetterOrderPay", // 律师函订单支付页面
  LAWYER_LETTER_ORDER_DETAIL = "lawyerLetterOrderDetail", // 律师函订单详情页面
  LAWYER_LETTER_ORDER_LIST = "lawyerLetterOrderList", // 律师函订单列表页面
  LAWYER_LETTER_COMMENT = "lawyerLetterComment", // 律师函评价页面
  LAWYER_LETTER_PAY = "lawyerLetterPay", // 律师函支付
  TO_INVOICE = "toInvoice", // 开发票
  LAWYER_LETTER_HOME = "lawyerLetterHome", // 律师函首页
  AFTER_SALE = "afterSale", // 售后
  OPEN_PROTOCOL = "openProtocol", // 打开协议
  LONG_PRESS_SAVE = "longPressSave", // 长按保存
  // 名片分享相关
  SHARE_CARD = "shareCard", // 分享名片
  TO_CHAT = "toChat", // 智聊
  TO_CONSULT = "toConsult", // 咨询
  CHOOSE_IMAGE = "chooseImage", // 选择图片
  ADD_CONTACT = "addContact", // 添加联系人
  COPY_TEXT = "copyText", // 复制文本
}

class JsBridge {
  private static lastCallTime: number;
  private static instance: JsBridge;

  static getInstance(): JsBridge {
    if (!JsBridge.instance) {
      JsBridge.instance = new JsBridge();
    }
    return JsBridge.instance;
  }
  private generateRandomName(): string {
    return `cb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private isEmpty(value: any): boolean {
    if (typeof value === "string") {
      return value.trim() === "";
    }
    if (typeof value === "object" && value !== null) {
      return Object.keys(value).length === 0;
    }
    return !value;
  }
  // 通用callNtive方法
  callClient(functionName: string, data?: CallbackData, callback?: (...args: any[]) => any): void {
    // 避免连续调用
    if (JsBridge.lastCallTime && Date.now() - JsBridge.lastCallTime < 100) {
      setTimeout(() => {
        this.callClient(functionName, data, callback);
      }, 100);
      return;
    }
    JsBridge.lastCallTime = Date.now();

    data = data || {};
    if (callback) {
      const cbName = this.generateRandomName();
      Object.assign(data, { cbName });
      window.jsClientCallBack[cbName] = callback.bind(this);
    }

    if (DeviceUtils.isIOS()) {
      window?.webkit?.messageHandlers?.jsBridge?.postMessage({
        action: functionName,
        params: {
          data: typeof data === "string" ? data : JSON.stringify(data),
        },
      });
      // window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers[functionName].postMessage(data);
    } else if (DeviceUtils.isAndroid()) {
      if (!this.isEmpty(data)) {
        const params = typeof data === "string" ? data : JSON.stringify(data);
        window.AndroidNativeApi &&
          window.AndroidNativeApi[functionName] &&
          window.AndroidNativeApi[functionName](params);
      } else {
        window.AndroidNativeApi && window.AndroidNativeApi[functionName] && window.AndroidNativeApi[functionName]();
      }
    } else {
      console.error("未获取platform信息，调取api失败");
    }
  }
  // 业务层自定义方法
  goBack(): void {
    if (DeviceUtils.isWeixin()) {
      // 使用类型断言绕过微信小程序 API 类型冲突
      const navigateBack = wx.miniProgram.navigateBack as any;
      navigateBack({
        delta: 1,
      });
    } else {
      this.callClient(ClientMethods.GO_BACK);
    }
  }
  askLawyer() {
    if (DeviceUtils.isWeixin()) {
      wx.miniProgram.navigateTo({
        url: "/module/ai-helper/chat/index?type=onAskLawyer",
      });
    } else {
      this.callClient(ClientMethods.ASK_LAWYER);
    }
  }
  getVoice() {
    // 调用客户端方法获取语音
    this.callClient(ClientMethods.GET_VOICE);
  }
  contractDownload(data: any) {
    if (DeviceUtils.isWeixin()) {
      const path = `/pages/base/download/index?url=${encodeURIComponent(data)}`;
      wx.miniProgram.navigateTo({
        url: path,
      });
    } else {
      this.callClient(ClientMethods.CONTRACT_DOWNLOAD, data);
    }
  }
  getVip() {
    if (DeviceUtils.isWeixin()) {
      const path = `/module/personal-center/buy-vip/index`;
      wx.miniProgram.navigateTo({
        url: path,
      });
    } else {
      this.callClient(ClientMethods.GET_VIP);
    }
  }
  openLink(data: any) {
    this.callClient(ClientMethods.OPEN_LINK, data);
  }
  openShare(data: any) {
    if (DeviceUtils.isWeixin()) {
      const path = `/pages/base/share/index?url=${encodeURIComponent(data)}`;
      wx.miniProgram.navigateTo({
        url: path,
      });
    } else {
      this.callClient(ClientMethods.OPEN_SHARE, data);
    }
  }
  applyStoreAuth() {
    this.callClient(ClientMethods.APPLY_STORE_AUTH);
  }
  goWxUpload(data: any) {
    if (DeviceUtils.isWeixin()) {
      wx.miniProgram.redirectTo({
        url: "/module/base/temporary-upload/index?type=1",
      });
    } else {
      this.callClient(ClientMethods.GO_WX_UPLOAD, data);
    }
  }
  longPressSave(data: any) {
    this.callClient(ClientMethods.LONG_PRESS_SAVE, data);
  }
  askByVoice(text: string) {
    EventEmitter.emit(Event.ASK_BY_VOICE, text);
  }
  openChangeFile() {
    EventEmitter.emit(Event.OPEN_CHANGE_FIEL);
  }
  reviewUpload(data: any) {
    EventEmitter.emit(Event.REVIEW_UPLOAD, data);
  }

  // 律师函订单支付页面
  lawyerLetterOrderPay(id?: any) {
    if (DeviceUtils.isWeixin()) {
      let path = `/module/lawyers-letter/order-confirm/index`;
      if (id) path += `?id=${id}`;
      wx.miniProgram.navigateTo({
        url: path,
      });
    } else {
      if (id) {
        this.callClient(ClientMethods.LAWYER_LETTER_ORDER_PAY, { data: { id } });
      } else {
        this.callClient(ClientMethods.LAWYER_LETTER_ORDER_PAY);
      }
    }
  }
  // 律师函订单列表页面
  lawyerLetterOrderList() {
    if (DeviceUtils.isWeixin()) {
      const path = `/module/lawyers-letter/order-list/index`;
      wx.miniProgram.navigateTo({
        url: path,
      });
    } else {
      this.callClient(ClientMethods.LAWYER_LETTER_ORDER_LIST);
    }
  }
  // 律师函评价页面
  lawyerLetterComment() {
    if (DeviceUtils.isWeixin()) {
      const path = `/module/lawyers-letter/comment/index`;
      wx.miniProgram.navigateTo({
        url: path,
      });
    } else {
      this.callClient(ClientMethods.LAWYER_LETTER_COMMENT);
    }
  }
  // 律师函订单详情页面
  lawyerLetterOrderDetail(id: any) {
    this.callClient(ClientMethods.LAWYER_LETTER_ORDER_DETAIL, { data: id });
  }
  // 律师函支付
  lawyerLetterPay(paymentMethod: any, payNo: string) {
    this.callClient(ClientMethods.LAWYER_LETTER_PAY, { data: { paymentMethod, payNo } });
  }
  // 开发票
  toInvoice(id: any) {
    if (DeviceUtils.isWeixin()) {
      wx.miniProgram.navigateTo({
        url: `/module/personal-center/create-invoice/index?id=${id}&type=lawyer`,
      });
    } else {
      this.callClient(ClientMethods.TO_INVOICE, { data: id });
    }
  }
  // 律师函首页
  lawyerLetterHome() {
    if (DeviceUtils.isWeixin()) {
      const path = `/module/lawyers-letter/index/index`;
      wx.miniProgram.reLaunch({
        url: path,
      });
    } else {
      this.callClient(ClientMethods.LAWYER_LETTER_HOME);
    }
  }
  // 保存二维码
  afterSale() {
    this.callClient(ClientMethods.AFTER_SALE);
  }
  // 打开协议
  openProtocol() {
    if (DeviceUtils.isWeixin()) {
      const path = `/module/lawyers-letter/protocol/index`;
      wx.miniProgram.navigateTo({
        url: path,
      });
    } else {
      this.callClient(ClientMethods.OPEN_PROTOCOL);
    }
  }

  // 分享名片
  shareCard(data: any) {
    this.callClient(ClientMethods.SHARE_CARD, data);
  }
  toChat(data: any) {
    this.callClient(ClientMethods.TO_CHAT, data);
  }
  toConsult(data: any) {
    this.callClient(ClientMethods.TO_CONSULT, data);
  }

  // 选择图片 - 调用原生选择器
  chooseImage(data: any) {
    this.callClient(ClientMethods.CHOOSE_IMAGE, data);
  }
  // 选择图片回调 - 原生调用此方法传递数据
  chooseImageCallback(base64Data: string) {
    EventEmitter.emit(Event.CHOOSE_IMAGE_CALLBACK, base64Data);
  }
  chooseImageCallbackAI(base64Data: string) {
    EventEmitter.emit(Event.CHOOSE_IMAGE_CALLBACK_AI, base64Data);
  }
  chooseImageCallbackLic(base64Data: string) {
    EventEmitter.emit(Event.CHOOSE_IMAGE_CALLBACK_LIC, base64Data);
  }
  // 添加联系人
  addContact(data: any) {
    this.callClient(ClientMethods.ADD_CONTACT, data);
  }
  // 复制文本
  copyText(data: any) {
    this.callClient(ClientMethods.COPY_TEXT, data);
  }
}

export default JsBridge.getInstance();
